'use client';

import { useTransition } from 'react';
import type { Dispatch } from 'react';
import {
  completedSubjects,
  selectedFiles,
  selectedCenterNumbers,
  selectedLocationSlugs,
  type ApplicationAction,
  type ApplicationState,
} from './applicationReducer';
import { submitForm } from '@/lib/api';
import { sourceUrlWithCampaign } from '@/lib/form/sourceUrl';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';

/**
 * Reads a File into the base64 string the backend forwards. Attachments travel
 * inline in the JSON body — there is no upload endpoint (AC 7).
 */
const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error(`Unexpected read result for ${file.name}`));
        return;
      }
      // `readAsDataURL` yields `data:<mime>;base64,<payload>` — send the payload.
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });

export function useApplicationSubmit(
  dispatch: Dispatch<ApplicationAction>,
  submitTracking: EventTrackingResolved[] | undefined,
  successTracking: EventTrackingResolved[] | undefined,
  customerNumber: string,
) {
  const [isPending, startTransition] = useTransition();

  const handleSubmit = (state: ApplicationState): Promise<void> =>
    new Promise((resolve) => {
      // Fires on tap, before the request completes (AC 10).
      pushCmsEventTracking(submitTracking);
      dispatch({ type: 'SUBMIT_START' });

      startTransition(async () => {
        try {
          const files = await Promise.all(
            selectedFiles(state).map(async (file) => ({
              filename: file.name,
              data: await toBase64(file),
            })),
          );

          await submitForm({
            type: 'application',
            salutation: state.personalData.salutation as 'mr' | 'mrs' | 'none',
            firstname: state.personalData.firstname,
            lastname: state.personalData.lastname,
            email: state.personalData.email,
            phone: state.personalData.phone,
            // Our field names — the rename to the downstream ones (`address`,
            // `housenumber`, `postcode`, `mobile`, `qualification`) happens
            // server-side in ApplicationSubmissionStrategy.
            street: state.personalData.street,
            houseNumber: state.personalData.houseNumber,
            postalCode: state.personalData.postalCode,
            city: state.personalData.city,
            ...(state.personalData.mobilePhone
              ? { mobilePhone: state.personalData.mobilePhone }
              : {}),
            locationSlugs: selectedLocationSlugs(state),
            // The downstream `schoolnumbers`, index-aligned with
            // `locationSlugs`. Sent by the client because the backend does no
            // slug lookup: step 2 only lets the applicant pick a Standort that
            // has one (see LocationPicker), so this is never empty here.
            schoolNumbers: selectedCenterNumbers(state),
            interestedInOnline: state.interestedInOnline,
            degree: state.degree,
            subjects: completedSubjects(state),
            ...(state.message ? { message: state.message } : {}),
            // Always sent, `[]` when nothing was attached: the downstream
            // endpoint returns 500 for an absent or null `files` key.
            files,
            sourceUrl: sourceUrlWithCampaign(),
            customerNumber,
          });
          // Only track the conversion when the request actually succeeded.
          pushCmsEventTracking(successTracking);
        } catch {
          // The applicant always reaches the confirmation (AC 8) — the backend
          // enqueues and reports success even when SH is unreachable,
          // so a failure here is ours to log, not theirs to see.
        }

        dispatch({ type: 'SUBMIT_SUCCESS' });
        resolve();
      });
    });

  return { handleSubmit, isPending };
}
